import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { fetchSprints, startSprint } from '../../store/sprintSlice';
import { fetchTasks } from '../../store/taskSlice';
import { fetchBoards, fetchBoard } from '../../store/boardSlice';
import { fetchWorkspace } from '../../store/workspaceSlice';
import { showToast } from '../../store/uiSlice';
import { useBoardMembers } from '../../hooks/useBoardMembers';
import { usePermissions } from '../../hooks/usePermissions';
import { Sprint } from '../../entities/sprint/model/types';
import SprintDashboardCard from '../../components/Sprint/SprintDashboardCard';
import SprintCreationModal from '../../components/Sprint/SprintCreationModal';

const STATUS_ORDER: Record<Sprint['status'], number> = {
  ACTIVE: 0,
  NOT_STARTED: 1,
  COMPLETED: 2,
};

const SprintDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { boardId: paramBoardId } = useParams<{ boardId: string }>();
  const { currentBoard, boards, loading: boardsLoading } = useAppSelector((state) => state.boards);
  const { sprints, loading: sprintsLoading } = useAppSelector((state) => state.sprints);
  const { tasks } = useAppSelector((state) => state.tasks);

  const effectiveBoardId = paramBoardId || currentBoard?.id;
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [startingSprintId, setStartingSprintId] = useState<string | null>(null);

  const { members: boardMembers } = useBoardMembers(effectiveBoardId);
  const { currentWorkspace } = useAppSelector((state) => state.workspaces);
  const { isOwnerOrAdmin: canManageSprint } = usePermissions(currentBoard?.workspaceId);

  useEffect(() => {
    if (!effectiveBoardId) {
      dispatch(fetchBoards());
    }
  }, [dispatch, effectiveBoardId]);

  // Arriving directly via /boards/:boardId/sprint-dashboard - currentBoard
  // may be stale or empty, and canManageSprint above needs its workspaceId.
  useEffect(() => {
    if (effectiveBoardId && currentBoard?.id !== effectiveBoardId) {
      dispatch(fetchBoard(effectiveBoardId));
    }
  }, [effectiveBoardId, currentBoard?.id, dispatch]);

  useEffect(() => {
    if (currentBoard?.workspaceId && currentWorkspace?.id !== currentBoard.workspaceId) {
      dispatch(fetchWorkspace(currentBoard.workspaceId));
    }
  }, [currentBoard?.workspaceId, currentWorkspace?.id, dispatch]);

  useEffect(() => {
    if (effectiveBoardId) {
      dispatch(fetchSprints(effectiveBoardId));
      dispatch(fetchTasks(effectiveBoardId));
    }
  }, [dispatch, effectiveBoardId]);

  const sortedSprints = useMemo(
    () => [...sprints].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]),
    [sprints]
  );

  const handleStart = async (sprintId: string) => {
    setStartingSprintId(sprintId);
    try {
      await dispatch(startSprint(sprintId)).unwrap();
      dispatch(showToast({ message: 'Sprint started', type: 'success' }));
    } catch (error: any) {
      dispatch(
        showToast({ message: error?.message || 'Failed to start sprint', type: 'error' })
      );
    } finally {
      setStartingSprintId(null);
    }
  };

  if (!effectiveBoardId) {
    return (
      <Container maxWidth="xs" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, borderRadius: 3 }} elevation={2}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Select a board
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose which board's sprints you'd like to view.
          </Typography>

          {boardsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          ) : boards.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              You don't have any boards yet. Create one from your workspace first.
            </Typography>
          ) : (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Board</InputLabel>
                <Select
                  value={selectedBoardId}
                  label="Board"
                  onChange={(e) => setSelectedBoardId(e.target.value)}
                >
                  {boards.map((board) => (
                    <MenuItem key={board.id} value={board.id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                fullWidth
                variant="contained"
                size="large"
                disabled={!selectedBoardId}
                onClick={() => dispatch(fetchBoard(selectedBoardId))}
              >
                View Sprint Dashboard
              </Button>
            </>
          )}
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {(currentWorkspace || currentBoard) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {currentWorkspace && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#6366f1',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    bgcolor: 'rgba(99, 102, 241, 0.08)',
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                  }}
                >
                  <WorkspacesOutlinedIcon sx={{ fontSize: 14 }} />
                  <span>{currentWorkspace.name}</span>
                </Box>
              )}

              {currentBoard && (
                <>
                  {currentWorkspace && (
                    <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
                      /
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#0f172a',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      bgcolor: 'rgba(15, 23, 42, 0.05)',
                      px: 1,
                      py: 0.25,
                      borderRadius: 1.5,
                    }}
                  >
                    <DashboardOutlinedIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                    <span>{currentBoard.name}</span>
                  </Box>
                </>
              )}
            </Box>
          )}

          <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <RocketLaunchIcon color="primary" fontSize="large" />
            Sprint Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track every sprint on this board in one place
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Sprint
        </Button>
      </Box>

      {sprintsLoading && sprints.length === 0 ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={180} sx={{ borderRadius: 3 }} />
          ))}
        </Stack>
      ) : sortedSprints.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <RocketLaunchIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            No sprints yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first sprint to start planning work for this board.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateModalOpen(true)}>
            Create Sprint
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {sortedSprints.map((sprint) => (
            <SprintDashboardCard
              key={sprint.id}
              sprint={sprint}
              tasks={tasks}
              boardMembers={boardMembers}
              actionLoading={startingSprintId === sprint.id}
              canManageSprint={canManageSprint}
              onStart={() => handleStart(sprint.id)}
              onComplete={() => navigate(`/boards/${effectiveBoardId}/sprints/${sprint.id}`)}
              onViewBoard={() => navigate(`/boards/${effectiveBoardId}/sprints/${sprint.id}`)}
              onViewInsights={() => navigate(`/boards/${effectiveBoardId}/sprints/${sprint.id}`)}
            />
          ))}
        </Stack>
      )}

      <SprintCreationModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        boardId={effectiveBoardId}
      />
    </Container>
  );
};

export default SprintDashboard;
